import React, { Suspense, lazy, useEffect } from "react";
import {
  Route,
  Routes,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import Login from "../Components/Account/Login/Login";
import ForgotPassword from "../Components/Account/ForgotPassword/ForgotPassword";
import AvisoPrivacidad from "../Components/Account/AvisoPrivacidad/AvisoPrivacidad";
import ProtectedRoute from "../Components/Generic/ProtectedRoute";
import RouteFallback from "../Components/Generic/RouteFallback";

const Home = lazy(() => import("../Components/Home/Home"));
const Users = lazy(() => import("../Components/Users/Users"));
const AddOrEditUser = lazy(() =>
  import("../Components/Users/AddOrEditUser/AddOrEditUser")
);
const PermissionsUser = lazy(() =>
  import("../Components/Users/PermissionsUser/PermissionsUser")
);
const Clients = lazy(() => import("../Components/Clients/Clients"));
const AddOrEditClient = lazy(() =>
  import("../Components/Clients/AddOrEditClient")
);
const Contact = lazy(() => import("../Components/Contact/Contact"));
const AddOrEditContact = lazy(() =>
  import("../Components/Contact/AddOrEditContact")
);
const Employees = lazy(() => import("../Components/Employees/Employees"));
const AddOrEditEmployee = lazy(() =>
  import("../Components/Employees/AddOrEditEmployee")
);
const Locations = lazy(() => import("../Components/Locations/Locations"));
const AddOrEditLocations = lazy(() =>
  import("../Components/Locations/AddOrEditLocations")
);
const Madrinas = lazy(() => import("../Components/Madrinas/Madrinas"));
const AddOrEditMadrina = lazy(() =>
  import("../Components/Madrinas/AddOrEditMadrina")
);
const Loads = lazy(() => import("../Components/Loads/Loads"));
const ImportLoads = lazy(() => import("../Components/Loads/ImportLoad"));
const DetailsLoads = lazy(() => import("../Components/Loads/DetailsLoads"));
const AddOrEditLoads = lazy(() => import("../Components/Loads/AddOrEditLoads"));
const CheckList = lazy(() => import("../Components/CheckList/CheckList"));
const AddOrEditChecklist = lazy(() =>
  import("../Components/CheckList/AddOrEditCheckList")
);
const Inspections = lazy(() => import("../Components/Inspections/Inspections"));
const InspectionGeneral = lazy(() =>
  import("../Components/InspectionGeneral/InspectionGeneral")
);
const DetailsInspectionGeneral = lazy(() =>
  import("../Components/InspectionGeneral/DetailsInspectionGeneral")
);
const Units = lazy(() => import("../Components/Units/Units"));
const AddOrEditUnits = lazy(() => import("../Components/Units/AddOrEditUnits"));
const ModelUnit = lazy(() => import("../Components/ModelUnit/ModelUnit"));
const AddOrEditModelUnit = lazy(() =>
  import("../Components/ModelUnit/AddOrEditModelUnit")
);
const ImportUnits = lazy(() => import("../Components/Units/ImportUnits"));
const InspectionPoint = lazy(() =>
  import("../Components/InspectionPoint/InspectionPoint")
);
const AddOrEditInspectionPoint = lazy(() =>
  import("../Components/InspectionPoint/AddOrEditInspectionPoint")
);
const DetailsInspections = lazy(() =>
  import("../Components/Inspections/DetailsInspections")
);
const ReportsInspections = lazy(() =>
  import("../Components/Reports/ReportsInspections/ReportsInspections")
);
const ReportDamageForInspector = lazy(() =>
  import("../Components/Reports/ReportsInspections/ReportDamageForInspector")
);
const ReportDamageForArea = lazy(() =>
  import("../Components/Reports/ReportsInspections/ReportDamageForArea")
);

const RoutesConfig = ({
  user,
  handleLogin,
  permissions,
  config,
  isAuthenticated,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated && location.pathname !== "/Account/Login") {
      localStorage.setItem("lastVisitedUrl", location.pathname);
    }
  }, [isAuthenticated, location.pathname, navigate]);

  useEffect(() => {
    const lastVisitedUrl = localStorage.getItem("lastVisitedUrl");
    if (isAuthenticated && lastVisitedUrl) {
      navigate(lastVisitedUrl);
    }
  }, [isAuthenticated, navigate]);

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route
          path="/Account/Login"
          element={
            user ? (
              <Navigate to="/" replace />
            ) : (
              <Login onLogin={handleLogin} config={config} />
            )
          }
        />

        <Route
          path="/Account/ForgotPassword"
          element={<ForgotPassword config={config} />}
        />
        <Route
          path="/Account/AvisoPrivacidad"
          element={<AvisoPrivacidad config={config} />}
        />

        <Route
          path="/"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Home className="content-section" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/Usuarios"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Users config={config} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/Usuarios/Agregar"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <AddOrEditUser config={config} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/Usuarios/Editar/:id"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <AddOrEditUser config={config} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/Usuarios/PermisosUsuarios/:id"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <PermissionsUser config={config} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/Clientes"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Clients config={config} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/Clientes/Agregar"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <AddOrEditClient config={config} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/Clientes/Editar/:id"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <AddOrEditClient config={config} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/Contactos"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Contact config={config} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/Contactos/Agregar"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <AddOrEditContact config={config} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/Contactos/Editar/:id"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <AddOrEditContact config={config} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/Empleados"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Employees config={config} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/Empleados/Agregar"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <AddOrEditEmployee config={config} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/Empleados/Editar/:id"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <AddOrEditEmployee config={config} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/Ubicaciones"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Locations config={config} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/Ubicaciones/Agregar"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <AddOrEditLocations config={config} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/Ubicaciones/Editar/:id"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <AddOrEditLocations config={config} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/Madrinas"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Madrinas config={config} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/Madrinas/Agregar"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <AddOrEditMadrina config={config} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/Madrinas/Editar/:id"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <AddOrEditMadrina config={config} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/Cargas"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Loads config={config} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/Cargas/ImportarCargas"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <ImportLoads config={config} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/Cargas/Detalle/:id"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <DetailsLoads config={config} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/Cargas/Agregar"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <AddOrEditLoads config={config} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/Cargas/Editar/:id"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <AddOrEditLoads config={config} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/CheckList"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <CheckList config={config} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/Checklist/Agregar"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <AddOrEditChecklist config={config} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/Checklist/Editar/:id"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <AddOrEditChecklist config={config} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/Inspecciones"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Inspections config={config} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/Inspecciones/Detalle/:id"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <DetailsInspections config={config} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/InspeccionGeneral"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <InspectionGeneral config={config} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/InspeccionGeneral/Detalle/:id"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <DetailsInspectionGeneral config={config} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/Unidades"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Units config={config} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/Unidades/Agregar"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <AddOrEditUnits config={config} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/Unidades/Editar/:id"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <AddOrEditUnits config={config} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/Unidades/ImportarUnidades"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <ImportUnits config={config} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/ModelosUnidad"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <ModelUnit config={config} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ModelosUnidad/Agregar"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <AddOrEditModelUnit config={config} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ModelosUnidad/Editar/:id"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <AddOrEditModelUnit config={config} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/PuntosInspeccion"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <InspectionPoint config={config} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/PuntosInspeccion/Editar/:id"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <AddOrEditInspectionPoint config={config} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/PuntosInspeccion/Agregar"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <AddOrEditInspectionPoint config={config} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/ReportesInspeccion"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <ReportsInspections config={config} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ReportesInspeccion/ReporteDeDañosPorInspector"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <ReportDamageForInspector config={config} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ReportesInspeccion/ReporteDeDañosPorArea"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <ReportDamageForArea config={config} />
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
  );
};

export default RoutesConfig;
